import { useEffect, useId, useRef, type MouseEvent, type ReactNode } from "react";
import { cx, usePresentation, type PresentationName } from "../presentation.tsx";
import { Icon, type IconName } from "../icons.tsx";

export interface NavItem {
  id: string;
  label: string;
  icon: IconName;
  href?: string;
  /** Count shown next to the item (e.g. unread exceptions). */
  count?: number;
  /** Console sidebar: items with the same group sit together under its heading (in nav order). */
  group?: string;
}

export interface AppShellProps {
  /** Brand slot: <Wordmark withMark /> in console, usually omitted on kiosk. */
  brand?: ReactNode;
  /** Page title in the top bar (console, mobile). */
  title?: ReactNode;
  nav?: NavItem[];
  activeId?: string;
  onNavigate?: (id: string, event: MouseEvent) => void;
  /** Top-bar right side: sync indicator, account menu. */
  topBar?: ReactNode;
  /** Console sidebar, under the brand: the workspace (site) switcher. */
  sideHeader?: ReactNode;
  /** Console sidebar, pinned to its foot. */
  sideFooter?: ReactNode;
  /** Override the presentation from context (the gallery uses this). */
  presentation?: PresentationName;
  children?: ReactNode;
  className?: string;
}

function NavLinks({ nav, activeId, onNavigate, className }: Pick<AppShellProps, "nav" | "activeId" | "onNavigate"> & { className: string }) {
  return (
    <ul className={className}>
      {nav?.map((item) => {
        const current = item.id === activeId;
        const content = (
          <>
            <Icon name={item.icon} />
            <span className="pmu-nav__label">{item.label}</span>
            {item.count ? <span className="pmu-nav__count" aria-label={`${item.count} new`}>{item.count}</span> : null}
          </>
        );
        const props = {
          className: cx("pmu-nav__item", current && "pmu-nav__item--current"),
          "aria-current": current ? ("page" as const) : undefined,
          onClick: onNavigate ? (e: MouseEvent) => onNavigate(item.id, e) : undefined,
        };
        return (
          <li key={item.id}>
            {item.href ? <a href={item.href} {...props}>{content}</a> : <button type="button" {...props}>{content}</button>}
          </li>
        );
      })}
    </ul>
  );
}

/** The console sidebar: items under their group headings (one list when no item has a group). */
function GroupedNav({ nav, activeId, onNavigate }: Pick<AppShellProps, "nav" | "activeId" | "onNavigate">) {
  const id = useId();
  const groups: { label: string; items: NavItem[] }[] = [];
  for (const item of nav ?? []) {
    const last = groups[groups.length - 1];
    if (last && last.label === (item.group ?? "")) last.items.push(item);
    else groups.push({ label: item.group ?? "", items: [item] });
  }
  if (groups.length <= 1 && !groups[0]?.label) return <NavLinks nav={nav} activeId={activeId} onNavigate={onNavigate} className="pmu-sidenav" />;
  return (
    <div className="pmu-sidenav__groups">
      {groups.map((g, i) => (
        <div key={`${g.label}-${i}`} className="pmu-sidenav__group" role={g.label ? "group" : undefined} aria-labelledby={g.label ? `${id}-${i}` : undefined}>
          {g.label && <div id={`${id}-${i}`} className="pmu-sidenav__heading">{g.label}</div>}
          <NavLinks nav={g.items} activeId={activeId} onNavigate={onNavigate} className="pmu-sidenav" />
        </div>
      ))}
    </div>
  );
}

/**
 * Page frame per presentation. console: sidebar + top bar. mobile: top bar + bottom tab bar
 * (with safe-area insets). kiosk: full-bleed content, nothing else on screen.
 */
export function AppShell({ brand, title, nav, activeId, onNavigate, topBar, sideHeader, sideFooter, presentation, children, className }: AppShellProps) {
  const fromContext = usePresentation();
  const p = presentation ?? fromContext;
  const mainId = useId();
  // The tab bar's height as --pmu-tabs-h on the shell, so anything pinned to the bottom of a page
  // (a message composer) sits above the bar instead of under it.
  const shellRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const tabs = tabsRef.current, shell = shellRef.current;
    if (!tabs || !shell || typeof ResizeObserver === "undefined") return;
    const set = () => shell.style.setProperty("--pmu-tabs-h", `${tabs.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(tabs);
    return () => ro.disconnect();
  }, [p, nav?.length]);
  // On a phone, the keyboard hides the tab bar, like the platform's own apps: a bar docked above
  // the tabs (a message composer) then sits on the keyboard. "Keyboard up" is a text field in focus
  // while the screen is shorter than without it; Android's back key closes the keyboard but keeps
  // the focus, and the tabs must come back then.
  useEffect(() => {
    const shell = shellRef.current;
    if (p !== "mobile" || !shell || typeof window === "undefined" || !window.matchMedia?.("(pointer: coarse)").matches) return;
    const typing = (el: Element | null) => el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && !["checkbox", "radio", "button", "submit", "range", "file", "color"].includes(el.type));
    let full = window.innerHeight;
    const update = () => {
      const focused = typing(document.activeElement);
      if (!focused) full = Math.max(full, window.innerHeight);
      shell.classList.toggle("pmu-shell--typing", focused && window.innerHeight < full - 120);
    };
    const later = () => setTimeout(update, 50);
    shell.addEventListener("focusin", later);
    shell.addEventListener("focusout", later);
    window.addEventListener("resize", update);
    const turn = () => { full = 0; setTimeout(update, 300); };
    window.addEventListener("orientationchange", turn);
    return () => { shell.removeEventListener("focusin", later); shell.removeEventListener("focusout", later); window.removeEventListener("resize", update); window.removeEventListener("orientationchange", turn); };
  }, [p]);

  if (p === "kiosk") {
    return <main className={cx("pmu-shell pmu-shell--kiosk", className)}>{children}</main>;
  }

  const header = (title || topBar || (p === "mobile" && brand)) && (
    <header className="pmu-shell__top">
      {p === "mobile" && brand}
      {title && <h1 className="pmu-shell__title">{title}</h1>}
      {topBar && <div className="pmu-shell__top-actions">{topBar}</div>}
    </header>
  );

  if (p === "mobile") {
    return (
      <div ref={shellRef} className={cx("pmu-shell pmu-shell--mobile", className)}>
        {header}
        <main className="pmu-shell__main" id={mainId}>{children}</main>
        {nav && nav.length > 0 && (
          <nav ref={tabsRef} className="pmu-shell__tabs" aria-label="Main">
            <NavLinks nav={nav} activeId={activeId} onNavigate={onNavigate} className="pmu-tabbar" />
          </nav>
        )}
      </div>
    );
  }

  return (
    <div className={cx("pmu-shell pmu-shell--console", className)}>
      <a className="pmu-skip" href={`#${mainId}`}>Skip to content</a>
      <aside className="pmu-shell__side">
        {brand && <div className="pmu-shell__brand">{brand}</div>}
        {sideHeader && <div className="pmu-shell__side-header">{sideHeader}</div>}
        {nav && (
          <nav aria-label="Main" className="pmu-shell__nav">
            <GroupedNav nav={nav} activeId={activeId} onNavigate={onNavigate} />
          </nav>
        )}
        {sideFooter && <div className="pmu-shell__side-footer">{sideFooter}</div>}
      </aside>
      <div className="pmu-shell__column">
        {header}
        <main className="pmu-shell__main" id={mainId} tabIndex={-1}>{children}</main>
      </div>
    </div>
  );
}
